import React from 'react'
import { ChevronsUpDown } from 'lucide-react'
import CommonAvatar from '@/shared/components/ui/Avatar/CommonAvatar'
import SecondaryButton from '@/shared/components/ui/Button/SecondaryButton'
import TruncatedText from '@/shared/components/ui/Text/TruncatedText'
import SimilarAccountActionButton from '@/features/dashboard/components/accountsDashboard/similarAccounts/SimilarAccountActionButton'
import { sanitizeCompanyLogoUrl } from '@/features/dashboard/utils/accountsDashboardUtils'
import { getAccountName } from './utils'

const CommonAvatarComponent = CommonAvatar as React.ComponentType<any>
const SecondaryButtonComponent = SecondaryButton as React.ComponentType<any>
const TruncatedTextComponent = TruncatedText as React.ComponentType<any>

const Bar = ({ className }: { className: string }) => (
    <div className={`h-3.5 rounded bg-gray-200 dark:bg-gray-800 animate-pulse ${className}`} />
)

/** Placeholder with the same size and alignment as the real cell in that column. */
export const SkeletonCell = ({ columnKey }: { columnKey: string }) => {
    switch (columnKey) {
        case 'company_name':
            return (
                <div className='flex items-center gap-2.5'>
                    <div className='w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse shrink-0' />
                    <Bar className='w-32' />
                </div>
            )
        case 'industry':
            return <Bar className='w-20' />
        case 'employee_size':
        case 'company_size':
            return <Bar className='w-24' />
        case 'revenue':
            return <Bar className='w-28' />
        case 'location':
            return <Bar className='w-40' />
        case 'actions':
            return (
                <div className='flex justify-center'>
                    <div className='w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-800 animate-pulse' />
                </div>
            )
        default:
            return <Bar className='w-16' />
    }
}

export const TextCell = ({ value }: { value?: React.ReactNode }) => (
    <TruncatedTextComponent text={value} className='text-sm font-normal text-gray-800 dark:text-gray-300' />
)

interface CompanyNameCellProps {
    account: any
    onOpen: (account: any) => void
}

/** Logo + name; clicking it (or the reveal-on-hover button) opens the details panel. */
export const CompanyNameCell = ({ account, onOpen }: CompanyNameCellProps) => {
    const name = getAccountName(account)
    return (
        <div className='flex items-center justify-between cursor-pointer group gap-3' onClick={() => onOpen(account)}>
            <div className='flex flex-1 items-center gap-2 min-w-0'>
                <CommonAvatarComponent
                    name={name}
                    imageUrl={sanitizeCompanyLogoUrl(account.company_logo || account.logo)}
                    size='sm'
                    className='w-8 h-8 rounded-lg text-xs font-medium text-secondary shrink-0 border-2 border-gray-100 bg-[#F2FCFF]'
                />
                <TruncatedTextComponent text={name} className='text-sm font-normal text-gray-800 dark:text-gray-100' />
            </div>
            <span className='group-hover:opacity-100 opacity-0 transition-opacity duration-300 shrink-0'>
                <SecondaryButtonComponent
                    title=''
                    className='!w-8 !h-8 !min-w-0 !p-0 !gap-0 rounded-lg flex items-center justify-center border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    icon={<ChevronsUpDown className='w-4 h-4 text-gray-500' />}
                    onClick={(e: React.MouseEvent) => {
                        e?.stopPropagation?.()
                        onOpen(account)
                    }}
                />
            </span>
        </div>
    )
}

/** renderCell for CustomTable: one switch, each cell its own small component.
 *  renderAction overrides the Actions cell (defaults to "add to accounts"). */
export const renderResultCell = (
    account: any,
    columnKey: string,
    onOpen: (account: any) => void,
    renderAction?: (account: any) => React.ReactNode,
) => {
    if (account?._isSkeleton) return <SkeletonCell columnKey={columnKey} />

    switch (columnKey) {
        case 'company_name':
            return <CompanyNameCell account={account} onOpen={onOpen} />
        case 'industry':
            return <TextCell value={account.industry} />
        case 'employee_size':
        case 'company_size':
            return <TextCell value={account.company_size || account.headcount} />
        case 'revenue':
            return <TextCell value={account.revenue || account.annual_revenue} />
        case 'location':
            return <TextCell value={account.location} />
        case 'actions':
            return (
                <div className='flex justify-center'>
                    {renderAction ? renderAction(account) : <SimilarAccountActionButton account={account} />}
                </div>
            )
        default:
            return <TextCell value={account[columnKey]} />
    }
}
